import React, { ReactNode } from "react";
import {
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { DateRange } from "@mui/lab";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { TimeField } from "@mui/x-date-pickers/TimeField";

export interface TimeRangeSelectProps {
  times: DateRange<Dayjs>;
  setTimes: React.Dispatch<React.SetStateAction<DateRange<Dayjs>>>;
  pattern: TimePattern;
  setPattern: React.Dispatch<React.SetStateAction<TimePattern>>;
}

export enum TimePattern {
  ALL = "ALLDAY",
  CUSTOM = "CUSTOM",
}

const TimeRangeSelect = ({
  times,
  setTimes,
  pattern,
  setPattern,
}: TimeRangeSelectProps): ReactNode => {
  const { t } = useTranslation();
  const handlePatternChange = (
    event: React.MouseEvent<HTMLElement>,
    newPattern: TimePattern,
  ) => {
    if (newPattern === null) {
      return;
    }
    setPattern(newPattern);
    switch (newPattern) {
      case TimePattern.ALL:
        setTimes([dayjs("0000", "HHmm"), dayjs("2359", "HHmm")]);
        break;
      case TimePattern.CUSTOM:
        break;
      default:
        break;
    }
  };

  return (
    <Stack direction={"column"}>
      <ToggleButtonGroup
        orientation="horizontal"
        exclusive
        value={pattern}
        size={"small"}
        onChange={handlePatternChange}
        color={"secondary"}
        sx={{
          display: "flex",
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          borderBottom: "0.5px solid black",
        }}
      >
        <ToggleButton
          key={"AllDay"}
          value={TimePattern.ALL}
          aria-label={t("allDay")}
          sx={{ borderBottomLeftRadius: 0, flex: 1 }}
        >
          {t("allDay")}
        </ToggleButton>
        <ToggleButton
          key={"Custom"}
          value={TimePattern.CUSTOM}
          aria-label={t("custom")}
          sx={{ borderBottomRightRadius: 0, flex: 1 }}
        >
          {t("custom")}
        </ToggleButton>
      </ToggleButtonGroup>
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        sx={{
          border: "1px solid",
          borderColor: "secondary.main",
          borderTop: "none",
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4,
          height: 44,
          px: 2,
        }}
      >
        <TimeField
          value={dayjs(times[0], "HHmm")}
          onChange={(newValue) => {
            setTimes([newValue, times[1]]);
          }}
          hiddenLabel={true}
          sx={{
            borderRadius: 0,
            flex: 1,
          }}
          slotProps={{
            textField: {
              variant: "standard",
              margin: "none",
              InputProps: {
                disableUnderline: true,
                sx: {
                  px: 6, // hack to center the text
                },
              },
            },
          }}
        />
        <Typography variant="body1" sx={{ px: 1 }}>
          {"-"}
        </Typography>
        <TimeField
          value={dayjs(times[1], "HHmm")}
          onChange={(newValue) => {
            setTimes([times[0], newValue]);
          }}
          hiddenLabel={true}
          sx={{
            borderRadius: 0,
            flex: 1,
          }}
          slotProps={{
            textField: {
              variant: "standard",
              margin: "none",
              InputProps: {
                disableUnderline: true,
                sx: {
                  px: 6, // hack to center the text
                },
              },
            },
          }}
        />
      </Stack>
    </Stack>
  );
};

export default TimeRangeSelect;
